/**
 * Bookkeeping for "X is joining…" notices (#1916).
 *
 * The backend broadcasts `participantAdmitted` to everyone in the call when a
 * waiting participant is let in. Until that participant's LiveKit identity
 * connects, everyone present sees one combined "joining" notice. If they have
 * not arrived within ADMISSION_TIMEOUT_MS, the notice quietly drops them — and
 * only the recipients (the room owner, or whoever admitted them) are told it
 * failed. A late arrival replaces a failure with "X joined", for the
 * recipients only.
 *
 * Pure state machine: the caller owns timers and rendering, and turns the
 * returned events into toasts.
 */

export const ADMISSION_TIMEOUT_MS = 30_000;

export interface Admission {
  participantId: string;
  /** LiveKit identity the participant will connect under. */
  identity: string;
  username: string;
  /** LiveKit identity of whoever admitted them. */
  admittedBy: string | null;
}

export type AdmissionEvent =
  /** The combined notice should show these people as still joining. */
  | { kind: "joining"; names: string[] }
  /** Nobody is joining any more; `joined` arrived during this batch. */
  | { kind: "batch-ended"; joined: string[] }
  /** Recipient-only: this admission timed out. */
  | { kind: "failed"; admission: Admission }
  /** Recipient-only: a timed-out admission arrived after all. */
  | { kind: "late-joined"; admission: Admission };

export class AdmissionTracker {
  private joining = new Map<string, Admission>();
  private failed = new Map<string, Admission>();
  private joinedInBatch: string[] = [];

  constructor(private readonly isRecipient: (admission: Admission) => boolean) {}

  /** A participantAdmitted message arrived. */
  admit(admission: Admission): AdmissionEvent[] {
    // Re-admitted after being put back: the earlier failure is moot.
    this.failed.delete(admission.identity);
    this.joining.set(admission.identity, admission);
    return [this.joiningEvent()];
  }

  /** A LiveKit participant connected. */
  arrive(identity: string): AdmissionEvent[] {
    const pending = this.joining.get(identity);
    if (pending) {
      this.joining.delete(identity);
      this.joinedInBatch.push(pending.username);
      return [this.afterJoiningChanged()];
    }
    const failed = this.failed.get(identity);
    if (failed) {
      this.failed.delete(identity);
      return [{ kind: "late-joined", admission: failed }];
    }
    return [];
  }

  /** The admission's timer ran out. */
  expire(identity: string): AdmissionEvent[] {
    const pending = this.joining.get(identity);
    if (!pending) return [];
    this.joining.delete(identity);
    const events: AdmissionEvent[] = [];
    if (this.isRecipient(pending)) {
      this.failed.set(identity, pending);
      events.push({ kind: "failed", admission: pending });
    }
    events.push(this.afterJoiningChanged());
    return events;
  }

  /** The recipient put this participant back in the waiting list. */
  forget(identity: string): void {
    this.failed.delete(identity);
  }

  isJoining(identity: string): boolean {
    return this.joining.has(identity);
  }

  private joiningEvent(): AdmissionEvent {
    return { kind: "joining", names: [...this.joining.values()].map((a) => a.username) };
  }

  private afterJoiningChanged(): AdmissionEvent {
    if (this.joining.size > 0) return this.joiningEvent();
    const joined = this.joinedInBatch;
    this.joinedInBatch = [];
    return { kind: "batch-ended", joined };
  }
}
